import {CognitoUser} from 'amazon-cognito-identity-js'
import {Auth} from 'aws-amplify'
import {getUserDataFresh} from './resolveSmsMfaStatus'

export const TOTP_ISSUER = 'OmicsBox'

/**
 * Whether an authenticator app (TOTP) is registered and enabled for this user, or null when the
 * lookup failed. Callers must not treat null as "off": offering setup to an enrolled user would
 * replace their secret and break the app they already use.
 */
export async function resolveTotpMfaEnabled(user: CognitoUser): Promise<boolean | null> {
  try {
    const data = await getUserDataFresh(user)
    if (data.PreferredMfaSetting === 'SOFTWARE_TOKEN_MFA') {
      return true
    }
    const list = data.UserMFASettingList
    return Array.isArray(list) && list.includes('SOFTWARE_TOKEN_MFA')
  } catch {
    return null
  }
}

/**
 * otpauth:// URI that authenticator apps read from the QR code.
 * Format: https://github.com/google/google-authenticator/wiki/Key-Uri-Format
 */
export function buildOtpAuthUri(secret: string, accountName: string): string {
  const issuer = encodeURIComponent(TOTP_ISSUER)
  const label = `${issuer}:${encodeURIComponent(accountName)}`
  return `otpauth://totp/${label}?secret=${encodeURIComponent(secret)}&issuer=${issuer}`
}

/** Asks Cognito for a new TOTP secret (AssociateSoftwareToken). */
export async function startTotpSetup(user: CognitoUser): Promise<string> {
  return Auth.setupTOTP(user)
}

function setTotpPreference(user: CognitoUser, enabled: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    // null leaves the SMS setting untouched.
    user.setUserMfaPreference(null, {PreferredMfa: enabled, Enabled: enabled}, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

/**
 * Verifies the first code from the app, then enables TOTP as the *preferred* method. Marking it
 * preferred matters when SMS is also on: without a preference Cognito answers sign-in with
 * SELECT_MFA_TYPE, which the hosted UI used by desktop and Salesforce handles poorly.
 */
export async function verifyAndEnableTotp(user: CognitoUser, code: string): Promise<void> {
  await Auth.verifyTotpToken(user, code)
  await setTotpPreference(user, true)
}

/** Turns off authenticator-app MFA; SMS MFA, if enabled, stays as it is. */
export async function disableTotpMfa(user: CognitoUser): Promise<void> {
  await setTotpPreference(user, false)
}
